import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationSubmissionStatus } from '@common/entities';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RootState } from 'state/store';

import { getSubmissionStatus } from 'services/responses';

interface UseSubmissionStatusOptions {
  responseId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const submissionStatusQueryKey = (responseId?: string) =>
  ['submission-status', responseId] as const;

export const useSubmissionStatus = ({
  responseId,
  enabled = true,
  refetchInterval = false,
}: UseSubmissionStatusOptions = {}) => {
  const currentResponseId = useSelector(
    (state: RootState) => responseId || state.application.applicant?.currentResponse?.responseId,
  );
  const queryClient = useQueryClient();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [checkAttempt, setCheckAttempt] = useState(0);

  const { data, isLoading, refetch, ...rest } = useQuery({
    queryKey: submissionStatusQueryKey(currentResponseId),
    queryFn: () => getSubmissionStatus(currentResponseId!),
    enabled: enabled && !!currentResponseId,
    refetchInterval: (query) =>
      query.state.data?.submissionStatus === ApplicationSubmissionStatus.Completed ||
      query.state.data?.submissionStatus === ApplicationSubmissionStatus.Failed
        ? false
        : refetchInterval,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const submissionStatus = data?.submissionStatus || null;
  const isSubmitting = submissionStatus === ApplicationSubmissionStatus.Submitting;
  const isCompleted = submissionStatus === ApplicationSubmissionStatus.Completed;
  const isFailed = submissionStatus === ApplicationSubmissionStatus.Failed;
  const isChecking = enabled && !!currentResponseId && !isCompleted && !isFailed;

  useEffect(() => {
    setHasTimedOut(false);
    if (!isChecking) return undefined;
    const timeout = window.setTimeout(() => setHasTimedOut(true), 120_000);
    return () => window.clearTimeout(timeout);
  }, [isChecking, currentResponseId, checkAttempt]);

  const retryStatusCheck = async () => {
    setCheckAttempt((attempt) => attempt + 1);
    await queryClient.cancelQueries({
      queryKey: submissionStatusQueryKey(currentResponseId),
      exact: true,
    });
    await refetch();
  };

  return {
    ...rest,
    submissionStatus,
    failureReason: data?.failureReason || null,
    hasStatusCheckIssue: isChecking && (rest.isError || hasTimedOut),
    isChecking,
    retryStatusCheck,
    submissionStartedAt: data?.submissionStartedAt || null,
    submissionCompletedAt: data?.submissionCompletedAt || null,
    isSubmitting,
    isCompleted,
    isFailed,
    isLoading,
    refetch,
  };
};
